/**
 * QuotesList — V2 quotes editor used by the Subcategory form.
 *
 *   <QuotesList
 *     quotes={state.quotes}
 *     onChange={(nextQuotes) => setState(prev => ({ ...prev, quotes: nextQuotes }))}
 *   />
 *
 * Each row is a multiline textarea + coral remove icon. An "Add
 * quote" button at the bottom appends an empty row. The shape
 * matches v1 (objects with `quote_text` and optional `speaker`)
 * so the submit payload is unchanged.
 */
import React from 'react';
import { Textarea } from '../../redesign/edit/TextInput';

const RemoveGlyph = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export default function QuotesList({ quotes, onChange }) {
  const safe = Array.isArray(quotes) ? quotes : [];

  const update = (next) => onChange?.(next);

  const updateAt = (index, patch) =>
    update(safe.map((q, i) => (i === index ? { ...q, ...patch } : q)));

  const removeAt = (index) =>
    update(safe.filter((_, i) => i !== index));

  const addRow = () => update([...safe, { speaker: '', quote_text: '' }]);

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {safe.length === 0 && (
        <div
          className="text-text-muted"
          style={{
            padding: 16,
            fontSize: 13,
            textAlign: 'center',
            background: '#FAF7F1',
            border: '1px dashed #E8E0CF',
            borderRadius: 8,
          }}
        >
          No quotes yet. Click <strong>+ Add quote</strong> to start.
        </div>
      )}
      {safe.map((quote, index) => (
        <div
          key={index}
          className="flex items-start"
          style={{
            gap: 10,
            padding: 12,
            background: '#FAF7F1',
            border: '1px solid #E8E0CF',
            borderRadius: 8,
          }}
        >
          <span
            className="font-mono text-text-muted"
            style={{ fontSize: 11, marginTop: 8, minWidth: 22, textAlign: 'right' }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Textarea
              value={quote.quote_text || ''}
              onChange={(e) => updateAt(index, { quote_text: e.target.value })}
              placeholder="Quote text…"
              rows={2}
            />
          </div>
          <button
            type="button"
            onClick={() => removeAt(index)}
            aria-label="Remove quote"
            className="grid place-items-center cursor-pointer"
            style={{
              width: 28,
              height: 28,
              background: 'transparent',
              border: '1px solid transparent',
              borderRadius: '50%',
              color: '#9CA1A8',
              transition: 'all 140ms ease',
              marginTop: 4,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#D64545';
              e.currentTarget.style.background = '#D6454514';
              e.currentTarget.style.borderColor = '#D6454540';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9CA1A8';
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            {RemoveGlyph}
          </button>
        </div>
      ))}
      <div>
        <button
          type="button"
          onClick={addRow}
          className="font-condensed font-bold uppercase cursor-pointer"
          style={{
            height: 32,
            padding: '0 14px',
            background: '#75A293',
            color: '#fff',
            border: 0,
            borderRadius: 6,
            fontSize: 12.5,
            letterSpacing: '0.06em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add quote
        </button>
      </div>
    </div>
  );
}
